<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import SettingsLayout from '../settings/SettingsLayout.vue';
import BaseSettingsHeader from '../settings/components/BaseSettingsHeader.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import Button from 'dashboard/components-next/button/Button.vue';
import CreateAccountModal from './CreateAccountModal.vue';

const { t } = useI18n();
const store = useStore();
const { accountScopedRoute } = useAccount();

const searchQuery = ref('');
const showCreateModal = ref(false);

const accounts = useMapGetter('superAdminAccounts/getSuperAdminAccounts');
const uiFlags = useMapGetter('superAdminAccounts/getUIFlags');

const fetchAccounts = () => {
  store.dispatch('superAdminAccounts/fetchAccounts', {
    search: searchQuery.value || undefined,
  });
};

onMounted(fetchAccounts);

let searchTimeout;
watch(searchQuery, () => {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(fetchAccounts, 300);
});

const tableHeaders = computed(() => [
  t('SUPER_ADMIN.ACCOUNTS.TABLE.NAME'),
  t('SUPER_ADMIN.ACCOUNTS.TABLE.STATUS'),
  t('SUPER_ADMIN.ACCOUNTS.TABLE.USERS'),
  t('SUPER_ADMIN.ACCOUNTS.TABLE.CREATED_AT'),
  t('SUPER_ADMIN.ACCOUNTS.TABLE.ACTIONS'),
]);

const toggleStatus = async account => {
  const nextStatus = account.status === 'active' ? 'suspended' : 'active';
  try {
    await store.dispatch('superAdminAccounts/updateAccountStatus', {
      id: account.id,
      status: nextStatus,
    });
    useAlert(t('SUPER_ADMIN.ACCOUNTS.STATUS_UPDATED'));
  } catch (error) {
    useAlert(error?.message || t('SUPER_ADMIN.ACCOUNTS.STATUS_UPDATE_ERROR'));
  }
};

const onAccountCreated = () => {
  // Don't close the modal here — CreateAccountModal stays open on its own to show the
  // generated password (shown once, never again). It emits @close itself once the SuperAdmin
  // clicks "Done" there. Just refresh the list in the background.
  fetchAccounts();
};

const formatDate = value => new Date(value).toLocaleDateString();
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.fetchingList"
    :loading-message="t('SUPER_ADMIN.ACCOUNTS.LOADING')"
    :no-records-found="!accounts.length"
    :no-records-message="t('SUPER_ADMIN.ACCOUNTS.NO_RECORDS')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="t('SUPER_ADMIN.ACCOUNTS.HEADER')"
        :description="t('SUPER_ADMIN.ACCOUNTS.DESCRIPTION')"
        :search-placeholder="t('SUPER_ADMIN.ACCOUNTS.SEARCH_PLACEHOLDER')"
      >
        <template #actions>
          <Button
            :label="t('SUPER_ADMIN.ACCOUNTS.NEW_ACCOUNT')"
            size="sm"
            @click="showCreateModal = true"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <BaseTable
        :headers="tableHeaders"
        :items="accounts"
        :no-data-message="t('SUPER_ADMIN.ACCOUNTS.NO_RECORDS')"
      >
        <template #row="{ items }">
          <BaseTableRow
            v-for="account in items"
            :key="account.id"
            :item="account"
          >
            <template #default>
              <BaseTableCell>
                <router-link
                  class="text-n-blue-text hover:underline"
                  :to="
                    accountScopedRoute('super_admin_account_show', {
                      superAdminAccountId: account.id,
                    })
                  "
                >
                  {{ account.name }}
                </router-link>
              </BaseTableCell>
              <BaseTableCell>
                <span
                  class="text-body-main"
                  :class="
                    account.status === 'active'
                      ? 'text-n-teal-11'
                      : 'text-n-ruby-11'
                  "
                >
                  {{
                    t(
                      `SUPER_ADMIN.ACCOUNTS.STATUS_${account.status.toUpperCase()}`
                    )
                  }}
                </span>
              </BaseTableCell>
              <BaseTableCell>{{ account.users_count }}</BaseTableCell>
              <BaseTableCell>
                {{ formatDate(account.created_at) }}
              </BaseTableCell>
              <BaseTableCell align="end">
                <Button
                  :label="
                    account.status === 'active'
                      ? t('SUPER_ADMIN.ACCOUNTS.SUSPEND')
                      : t('SUPER_ADMIN.ACCOUNTS.ACTIVATE')
                  "
                  slate
                  sm
                  :is-loading="uiFlags.updatingItem"
                  @click="toggleStatus(account)"
                />
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <woot-modal
      v-model:show="showCreateModal"
      :on-close="() => (showCreateModal = false)"
    >
      <CreateAccountModal
        @close="showCreateModal = false"
        @created="onAccountCreated"
      />
    </woot-modal>
  </SettingsLayout>
</template>
